import { Col, Heading, Row } from '@zoom/components';
import { addDays, differenceInDays, startOfToday, startOfDay, min } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { TFunction } from 'i18next';
import { isEmpty } from 'ramda';
import React, { Key, useEffect, useState } from 'react';
import { Field, FormRenderProps, FieldProps } from 'react-final-form';
import { OnChange } from 'react-final-form-listeners';
import { toast } from 'react-toastify';
import { FormGroup } from 'reactstrap';

import { DatePickerField } from '../../../components/DatePicker';
import { ModalConfirmation } from '../../../components/ModalConfirmation';
import { QueueHistoryRangeQuery } from '../../../graphql/components';
import { QueueHistoryRangeResult } from '../../../graphql/types';
import { RecordBooleanType } from '../../../types';
import { createEndOfDateRange } from '../../../utils/dateRange';
import { required, nextDayInWeek, previousDayInWeek } from '../../../utils/validations';
import { composeValidators } from '../../../utils/validations/composeValidators';
import { withinRange } from '../../../utils/validations/withinRange';
import { Adjustment } from '../config';
import {
  HISTORICAL_DATA_START_DATE_INPUT,
  HISTORICAL_DATA_END_DATE_INPUT,
  FORECAST_START_DATE_INPUT,
  FORECAST_END_DATE_INPUT,
  TWO_WEEKS_IN_DAYS,
} from '../constants';
import { amendAdjustmentsInCollision, getAdjustmentsInCollision } from '../utils';
import { FormValues } from '../utils/values';
import {
  ForecastRangeDateFieldProps,
  getForecastEndDateTimeFieldProps,
  getForecastStartDateTimeFieldProps,
  getHistoryStartDateTimeFieldProps,
  getHistoryEndDateTimeFieldProps,
  RangeInput,
} from './forecastRangeUtils';

interface RangeFieldProps {
  dataTestId: string;
  name: string;
  t: TFunction;
  inputProps: RangeInput;
  loading: boolean;
  disabled: boolean;
  validate?: FieldProps<Date, any>['validate'];
  rerenderId?: Key;
}

const RangeField: React.FC<RangeFieldProps> = ({
  dataTestId,
  name,
  t,
  inputProps,
  loading,
  disabled,
  validate,
  rerenderId,
}) => {
  const datePicker = DatePickerField({
    dataTestId,
    dateFormat: t('date.formats.default'),
    disabled: inputProps.disabled || loading || disabled,
    endDate: inputProps.endDate,
    selectableWeekDays: inputProps.selectableWeekDays,
    label: t(`date.date${name.match(/startDateTime/) ? 'From' : 'To'}`),
    maxDate: inputProps.maxDate,
    maxDateRangeOverflow: inputProps.maxDateRangeOverflow,
    minDate: inputProps.minDate,
    minDateRangeOverflow: inputProps.minDateRangeOverflow,
    selectsEnd: inputProps.selectsEnd,
    selectsStart: inputProps.selectsStart,
    startDate: inputProps.startDate,
  });

  return <Field key={rerenderId} name={name} component={datePicker} validate={validate} />;
};

type AdjustmentCollisionModal =
  | { open: false }
  | {
      open: true;
      adjustmentsInCollision: RecordBooleanType;
      prevRangeInputValues: {
        startDateTime: Date;
        endDateTime: Date;
      };
      newRangeValues: {
        startDateTime: Date;
        endDateTime: Date;
      };
    };

const initModal: AdjustmentCollisionModal = {
  open: false,
};

type FormForecastRangeProps = FormRenderProps<FormValues> & {
  adjustments: Adjustment[];
  updateAdjustments: (adjustments: Adjustment[]) => void;
  queueTimezone: string;
  queueHistoryRange: QueueHistoryRangeResult;
  t: TFunction;
  isEdit: boolean;
  isView: boolean;
};

const isHistoryDataCoveringFullTwoWeeksRange = (data: QueueHistoryRangeQuery) => {
  const endDateTime = data?.queueHistoryRange?.endDateTime;
  const startDateTime = data?.queueHistoryRange?.startDateTime;

  if (!endDateTime || !startDateTime) {
    return false;
  }

  const startDate = new Date(startDateTime);

  const startOfStartDateTime = startOfDay(startDate);
  const startOfEndDateTime = startOfDay(new Date(endDateTime));

  const minStartDate =
    startOfStartDateTime.getTime() === startDate.getTime() ? startOfStartDateTime : addDays(startOfStartDateTime, 1);

  if (differenceInDays(min([startOfEndDateTime, startOfToday()]), minStartDate) < TWO_WEEKS_IN_DAYS) {
    return false;
  }
  return true;
};

export const FormForecastRange: React.FC<FormForecastRangeProps> = ({
  t,
  isEdit,
  isView,
  queueTimezone,
  queueHistoryRange,
  values,
  adjustments,
  updateAdjustments,
  form,
}) => {
  const [hasHistoryData, setHasHistoryData] = useState(false);
  const [adjustmentCollisionModal, setAdjustmentCollisionModal] = useState<AdjustmentCollisionModal>(initModal);

  useEffect(() => {
    const { error, data, loading } = queueHistoryRange;

    if (!loading) {
      const checkHistoryData = () => {
        if (error || !data || !data.queueHistoryRange) {
          return false;
        }

        const result = isHistoryDataCoveringFullTwoWeeksRange(data);

        if (!result) {
          toast.warn(t('forecast.notEnoughHistoricalData'));
        }
        return result;
      };

      setHasHistoryData(checkHistoryData());
    }
  }, [queueHistoryRange, queueTimezone, t]);

  const {
    historyRange: { endDateTime: historyEndDateTime, startDateTime: historyStartDateTime } = {
      endDateTime: undefined,
      startDateTime: undefined,
    },
  } = values;

  const rangeFieldDisabled = isEdit || isView;
  const forecastStartDateKey = historyEndDateTime ? historyEndDateTime.toString() : undefined;

  const commonDateTimeFieldProps: ForecastRangeDateFieldProps = {
    hasHistoryData,
    historyRange: queueHistoryRange,
    disabled: rangeFieldDisabled,
    values,
    queueTimezone,
  };

  const handleSubmitCollisionModal = () => {
    if (adjustmentCollisionModal.open) {
      const { adjustmentsInCollision, newRangeValues } = adjustmentCollisionModal;

      const newAdjustments = amendAdjustmentsInCollision(
        adjustments,
        zonedTimeToUtc(newRangeValues.startDateTime, queueTimezone),
        zonedTimeToUtc(newRangeValues.endDateTime, queueTimezone),
        adjustmentsInCollision
      );

      updateAdjustments(newAdjustments);

      // Closing modal and resetting modal values
      setAdjustmentCollisionModal(initModal);
    }
  };

  const handleCloseCollisionModal = () => {
    if (adjustmentCollisionModal.open) {
      // Resetting back Forecast Range values
      form.change('forecastRange', {
        startDateTime: adjustmentCollisionModal.prevRangeInputValues.startDateTime,
        endDateTime: adjustmentCollisionModal.prevRangeInputValues.endDateTime,
      });

      // Closing modal and resetting modal values
      setAdjustmentCollisionModal(initModal);
    }
  };

  return (
    <div className="form-column-l">
      <OnChange name="forecastRange.startDateTime">
        {(value, previous) => {
          if (value !== previous && adjustments.length) {
            // Current endDateTime value
            const {
              forecastRange: { endDateTime },
            } = values;

            // End Date with open interval for forecast Range
            const endDateTimeRange = createEndOfDateRange(endDateTime!);

            const adjustmentsInCollision = getAdjustmentsInCollision(
              adjustments,
              zonedTimeToUtc(value, queueTimezone),
              zonedTimeToUtc(endDateTimeRange, queueTimezone)
            );

            if (!isEmpty(adjustmentsInCollision)) {
              setAdjustmentCollisionModal({
                adjustmentsInCollision,
                open: true,
                prevRangeInputValues: {
                  startDateTime: previous,
                  endDateTime: endDateTime!,
                },
                newRangeValues: {
                  startDateTime: value,
                  endDateTime: endDateTimeRange,
                },
              });
            }
          }
        }}
      </OnChange>
      <OnChange name="forecastRange.endDateTime">
        {(value, previous) => {
          if (value !== previous && adjustments.length) {
            // Current startDateTime value
            const {
              forecastRange: { startDateTime },
            } = values;

            // End Date with open interval for forecast Range
            const newEndDateTimeRange = createEndOfDateRange(value);

            const adjustmentsInCollision = getAdjustmentsInCollision(
              adjustments,
              zonedTimeToUtc(startDateTime!, queueTimezone),
              zonedTimeToUtc(newEndDateTimeRange, queueTimezone)
            );

            if (!isEmpty(adjustmentsInCollision)) {
              setAdjustmentCollisionModal({
                adjustmentsInCollision,
                open: true,
                prevRangeInputValues: {
                  startDateTime: startDateTime!,
                  endDateTime: previous,
                },
                newRangeValues: {
                  startDateTime: startDateTime!,
                  endDateTime: newEndDateTimeRange,
                },
              });
            }
          }
        }}
      </OnChange>
      <FormGroup>
        <div className="create-forecast__section-title__wrapper">
          <Heading kind="section">{t('forecast.historicalDataRange')}</Heading>
        </div>
        <Row>
          <Col xs="6">
            <RangeField
              dataTestId={HISTORICAL_DATA_START_DATE_INPUT}
              name="historyRange.startDateTime"
              inputProps={getHistoryStartDateTimeFieldProps(commonDateTimeFieldProps)}
              loading={queueHistoryRange.loading}
              t={t}
              disabled={rangeFieldDisabled}
              validate={
                !rangeFieldDisabled && historyEndDateTime
                  ? composeValidators(required, nextDayInWeek('historyRange.endDateTime'))
                  : required
              }
            />
          </Col>
          <Col xs="6">
            <RangeField
              dataTestId={HISTORICAL_DATA_END_DATE_INPUT}
              name="historyRange.endDateTime"
              inputProps={getHistoryEndDateTimeFieldProps(commonDateTimeFieldProps)}
              loading={queueHistoryRange.loading}
              t={t}
              disabled={rangeFieldDisabled}
              validate={
                !rangeFieldDisabled && historyStartDateTime
                  ? composeValidators(required, previousDayInWeek('historyRange.startDateTime'))
                  : required
              }
            />
          </Col>
        </Row>
      </FormGroup>
      <FormGroup>
        <div className="create-forecast__section-title__wrapper">
          <Heading kind="section">{t('forecast.forecastRange')}</Heading>
        </div>
        <Row>
          <Col xs="6">
            <RangeField
              rerenderId={forecastStartDateKey}
              dataTestId={FORECAST_START_DATE_INPUT}
              name="forecastRange.startDateTime"
              inputProps={getForecastStartDateTimeFieldProps(commonDateTimeFieldProps)}
              loading={queueHistoryRange.loading}
              t={t}
              disabled={rangeFieldDisabled}
              validate={composeValidators(
                required,
                withinRange(historyEndDateTime ? addDays(historyEndDateTime, 1) : startOfToday(), undefined)
              )}
            />
          </Col>
          <Col xs="6">
            <RangeField
              dataTestId={FORECAST_END_DATE_INPUT}
              name="forecastRange.endDateTime"
              inputProps={getForecastEndDateTimeFieldProps(commonDateTimeFieldProps)}
              loading={queueHistoryRange.loading}
              t={t}
              disabled={rangeFieldDisabled}
              validate={required}
            />
          </Col>
        </Row>
      </FormGroup>
      {adjustmentCollisionModal.open && (
        <ModalConfirmation
          bodyText={t('forecast.modal.adjustmentsCollisionBody')}
          closeText={t('layout.action.cancel')}
          headerText={t('layout.warning')}
          onClose={handleCloseCollisionModal}
          onSubmit={handleSubmitCollisionModal}
          show
          submitText={t('layout.action.proceed')}
        />
      )}
    </div>
  );
};
